import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';

@Component({
  selector: 'app-toggleable-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toggleable-button.component.html',
  styleUrl: './toggleable-button.component.scss'
})

export class ToggleableComponent {
  // Input: Current state (true = enabled, false = disabled)
  @Input() isEnabled = true;
  
  // Input: Loading state (prevents clicking while processing)
  @Input() isLoading = false;
  
  // Input: Custom labels
  @Input() enabledLabel = 'Enable';
  @Input() disabledLabel = 'Disable';
  
  // Input: Show icons
  @Input() showIcons = true;
  
  // Input: Custom icon names (material icons)
  @Input() enabledIcon = 'check_circle';
  @Input() disabledIcon = 'cancel';
  @Input() loadingIcon = 'hourglass_empty';
  
  // Input: Custom CSS classes for different states
  @Input() enabledClass = 'bg-green-500 hover:bg-green-600 text-white';
  @Input() disabledClass = 'bg-red-500 hover:bg-red-600 text-white';
  @Input() baseClass = 'px-4 py-2 rounded-md font-medium text-sm transition-all duration-300';
  @Input() loadingClass = 'opacity-50 cursor-not-allowed';
  @Input() iconClass = 'text-base';
  
  // Output: Emits when state changes
  @Output() stateChange = new EventEmitter<boolean>();
  
  // Output: Emits toggle event with current state
  @Output() toggleState = new EventEmitter<{ newState: boolean; previousState: boolean }>();

  get buttonClasses(): string {
    const stateClass = this.isEnabled ? this.enabledClass : this.disabledClass;
    const loadingStateClass = this.isLoading ? this.loadingClass : '';
    
    return `${this.baseClass} ${stateClass} ${loadingStateClass}`.trim();
  }

  get currentIcon(): string {
    if (this.isLoading) {
      return this.loadingIcon;
    }
    return this.isEnabled ? this.enabledIcon : this.disabledIcon;
  }

  get buttonText(): string {
    if (this.isLoading) {
      return this.isEnabled ? 'Disabling...' : 'Enabling...';
    }
    return this.isEnabled ? this.disabledLabel : this.enabledLabel;
  }

  onToggle(): void {
    if (this.isLoading) {
      return; // Prevent toggle while loading
    }

    const previousState = this.isEnabled;
    const newState = !this.isEnabled;
    
    // Emit events
    this.stateChange.emit(newState);
    this.toggleState.emit({ newState, previousState });
  }
}
